import { Accessibility, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAccessibility, A11yMode } from "@/hooks/useAccessibility";
import { cn } from "@/lib/utils";

interface AccessibilityMenuProps {
  /** Render variant for placement. */
  appearance?: "header" | "footer";
  className?: string;
}

const OPTIONS: { value: A11yMode; label: string; hint: string }[] = [
  { value: "auto", label: "Auto", hint: "Match my device" },
  { value: "on", label: "On", hint: "Always use accessible colours" },
  { value: "off", label: "Off", hint: "Use standard colours" },
];

export const AccessibilityMenu = ({ appearance = "header", className }: AccessibilityMenuProps) => {
  const { mode, isOn, setMode } = useAccessibility();

  const triggerLabel = isOn ? "Accessibility mode on" : "Accessibility mode";

  return (
    <Popover>
      <PopoverTrigger asChild>
        {appearance === "footer" ? (
          <button
            type="button"
            className={cn(
              "inline-flex items-center gap-2 text-sm text-primary-foreground/80 hover:text-primary-foreground underline-offset-4 hover:underline transition-colors",
              className,
            )}
            aria-label={triggerLabel}
            aria-pressed={isOn}
          >
            <Accessibility className="w-4 h-4" />
            <span>Accessibility</span>
          </button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className={cn("rounded-full", className)}
            aria-label={triggerLabel}
            aria-pressed={isOn}
            title={triggerLabel}
          >
            <Accessibility className="w-5 h-5" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-4">
        <div className="space-y-3">
          <div>
            <h3 className="font-display text-base font-semibold text-foreground">
              Accessibility mode
            </h3>
            <p className="text-xs text-foreground/70 mt-1">
              High-contrast colours and clearer icons for low-vision and colour-blind readers.
            </p>
          </div>
          <div role="radiogroup" aria-label="Accessibility mode" className="flex flex-col gap-1">
            {OPTIONS.map((opt) => {
              const selected = mode === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setMode(opt.value)}
                  className={cn(
                    "flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                    selected
                      ? "border-primary bg-primary/5 text-foreground"
                      : "border-border hover:bg-muted text-foreground/85",
                  )}
                >
                  <span>
                    <span className="font-medium">{opt.label}</span>
                    <span className="block text-xs text-foreground/60">{opt.hint}</span>
                  </span>
                  {selected && <Check className="w-4 h-4 text-primary" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-foreground/55">
            Status: {isOn ? "On" : "Off"}{mode === "auto" ? " (auto)" : ""}
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};
